// ---------------------------------------------------------------------------
// usePickListLiveRefresh — live-invalidate a pick-list detail query
//
// Opens a lightweight SSE subscription to the warehouse-level live stream
// and invalidates the cached pick-list detail (and its audit) whenever an
// event that may have changed its task/mission state arrives. Coalesces
// bursts with a short debounce so a rapid sequence of voice-gateway
// events doesn't fan out into a refetch per event.
// ---------------------------------------------------------------------------

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRequestLiveTicket } from '../api/live';

const SSE_BASE = '/sse';
const DEBOUNCE_MS = 400;

// Events that signal task / mission progress (no pick_list_id in payload —
// we can't filter, so any such event invalidates).
const SESSION_SCOPED_EVENTS = [
  'task.opened',
  'task.confirmed',
  'task.skipped',
  'task.forced',
  'fsm.transition',
  'session.started',
  'session.ended',
] as const;

// Events that carry pick_list_id — filter to those that match.
const PICK_LIST_SCOPED_EVENTS = [
  'pick_list.imported',
  'pick_list.assigned',
  'pick_list.confirmed',
  'pick_list.cancelled',
  'mission.state_changed',
] as const;

export function usePickListLiveRefresh(
  token: string | null,
  tenantCode: string | null,
  pickListId: number | null,
  warehouseId: number | null,
): void {
  const qc = useQueryClient();
  const ticketMut = useRequestLiveTicket(token, tenantCode);
  const esRef = useRef<EventSource | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!token || !tenantCode || !pickListId || !warehouseId) return;

    const schedule = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['admin', 'pick-lists', 'detail', pickListId] });
        qc.invalidateQueries({ queryKey: ['admin', 'audit', 'tenant'] });
      }, DEBOUNCE_MS);
    };

    let cancelled = false;
    ticketMut
      .mutateAsync({ warehouseId })
      .then((ticket) => {
        if (cancelled) return;
        const params = new URLSearchParams({
          ticket: ticket.ticket,
          warehouse_id: String(warehouseId),
        });
        const es = new EventSource(`${SSE_BASE}/live/stream?${params.toString()}`);
        esRef.current = es;

        SESSION_SCOPED_EVENTS.forEach((evType) => {
          es.addEventListener(evType, () => schedule());
        });
        PICK_LIST_SCOPED_EVENTS.forEach((evType) => {
          es.addEventListener(evType, (rawEv: Event) => {
            const ev = rawEv as MessageEvent;
            try {
              const data = JSON.parse(ev.data as string) as { pick_list_id?: number };
              if (data.pick_list_id === pickListId) schedule();
            } catch {
              // malformed payload — drop
            }
          });
        });

        // Errors silently close — the user can refresh manually. We don't
        // implement reconnect-with-backoff here because the detail page
        // is short-lived; the next mount opens a new ticket.
      })
      .catch(() => { /* ticket fetch failed — silent, no live updates */ });

    return () => {
      cancelled = true;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
    // ticketMut is stable across renders for the same (token, tenantCode)
    // tuple, so we intentionally exclude it from deps to avoid reopening
    // the stream on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tenantCode, pickListId, warehouseId, qc]);
}
